/**
 * A2 delivery-fee policy, mirrored from the backend for previews only.
 *
 * The backend sets the fee of every new order (all channels) and of every edit to a
 * policy-priced order. The order wizard sends the fee it shows as
 * `expectedShippingFeeAmount`; the backend refuses the order (409) if its own fee differs,
 * so staff never save a total they did not see. Keep in step with
 * GET /api/v1/orders/shipping-policy (pinned in shippingPolicy.test.js).
 */
export const SHIPPING_POLICY = {
  version: "A2-2026-11",
  currencyCode: "EUR",
  rules: [
    { country: "Kosove", feeAmount: 2, freeAboveAmount: 50 },
    { country: "Shqipëri", feeAmount: 5, freeAboveAmount: null },
    { country: "Maqedoni", feeAmount: 5, freeAboveAmount: null },
  ],
};

export const SHIPPING_FEE_OVERRIDE_REASON_MAX = 500;

const cents = (amount) => Math.round(Number(amount) * 100);

/** Policy fee for an items total at the prices charged; null for a country the policy does not serve. */
export function policyShippingFee(country, itemsTotal) {
  const rule = SHIPPING_POLICY.rules.find((candidate) => candidate.country === country);
  if (!rule) return null;
  if (rule.freeAboveAmount !== null && cents(itemsTotal) > cents(rule.freeAboveAmount)) return 0;
  return cents(rule.feeAmount) / 100;
}

/** Parses a money input ("2", "2,5"). Empty -> null, garbage -> NaN. */
function parseFee(value) {
  if (value === "" || value === null || value === undefined) return null;
  const parsed = Number(String(value).trim().replace(",", "."));
  return Number.isFinite(parsed) ? Math.round(parsed * 100) / 100 : Number.NaN;
}

/** Errors for a manager override: an amount of 0 or more and a reason are both required. */
export function validateShippingFeeOverride({ enabled, amount, reason }) {
  if (!enabled) return { amount: null, reason: null };
  const parsed = parseFee(amount);
  const trimmed = String(reason || "").trim();
  return {
    amount: parsed === null || Number.isNaN(parsed) || parsed < 0 ? "storefrontOrders.shippingFeeInvalid" : null,
    reason: !trimmed
      ? "storefrontOrders.shippingFeeOverrideReasonRequired"
      : trimmed.length > SHIPPING_FEE_OVERRIDE_REASON_MAX
        ? "storefrontOrders.shippingFeeOverrideReasonTooLong"
        : null,
  };
}

/** The fee to show: the override when one is set, otherwise the policy fee (0 if unknown). */
export function effectiveShippingFee({ country, itemsTotal, override }) {
  if (override?.enabled) return Math.max(0, parseFee(override.amount) || 0);
  return policyShippingFee(country, itemsTotal) ?? 0;
}

export function shippingFeeOverridePayload(override) {
  return { amount: parseFee(override.amount), reason: String(override.reason || "").trim() };
}

/** Where the fee of a saved order comes from, for the order detail. */
export function describeShippingFeeSource(order, t) {
  if (!order?.shippingFeePolicy) return t("storefrontOrders.shippingFeeLegacy");
  if (order.shippingFeeOverrideReason) {
    return `${t("storefrontOrders.shippingFeeOverridden")}: ${order.shippingFeeOverrideReason}`;
  }
  return t("storefrontOrders.shippingFeeByPolicy");
}
